import { useCallback } from 'react';
import { useReactFlow, type Viewport } from '@xyflow/react';

/** Convert ReactFlow node position to screen coordinates */
function flowToScreen(flowX: number, flowY: number, viewport: Viewport): { x: number; y: number } {
  return {
    x: flowX * viewport.zoom + viewport.x,
    y: flowY * viewport.zoom + viewport.y,
  };
}

/**
 * Hook to resolve a demo step target (nodeId or selector) to screen coordinates.
 * Must be used inside a ReactFlowProvider.
 */
export function useDemoCoordinates() {
  const { getNode, getViewport, fitView } = useReactFlow();

  /** Get the center screen coordinates of a ReactFlow node */
  const getNodeScreenCenter = useCallback(
    (nodeId: string): { x: number; y: number } | null => {
      // Prefer DOM measurement (accounts for actual rendered size)
      const el = document.querySelector(`[data-id="${nodeId}"]`);
      if (el) {
        const rect = el.getBoundingClientRect();
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      }
      // Fallback to flow coordinate conversion
      const node = getNode(nodeId);
      if (!node) return null;
      const viewport = getViewport();
      const pos = flowToScreen(node.position.x, node.position.y, viewport);
      return { x: pos.x + 150, y: pos.y + 100 }; // rough center offset
    },
    [getNode, getViewport],
  );

  /** Get the bounding rect of a ReactFlow node element */
  const getNodeRect = useCallback((nodeId: string): DOMRect | null => {
    const el = document.querySelector(`[data-id="${nodeId}"]`);
    return el ? el.getBoundingClientRect() : null;
  }, []);

  /** Get the bounding rect of an element by CSS selector */
  const getSelectorRect = useCallback((selector: string): DOMRect | null => {
    const el = document.querySelector(selector);
    return el ? el.getBoundingClientRect() : null;
  }, []);

  /** Get the center of an element by CSS selector */
  const getSelectorCenter = useCallback((selector: string): { x: number; y: number } | null => {
    const el = document.querySelector(selector);
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }, []);

  /** Resolve any target to screen center coords */
  const resolveTargetCenter = useCallback(
    (target: { nodeId?: string; selector?: string }): { x: number; y: number } | null => {
      if (target.nodeId) return getNodeScreenCenter(target.nodeId);
      if (target.selector) return getSelectorCenter(target.selector);
      return null;
    },
    [getNodeScreenCenter, getSelectorCenter],
  );

  /** Resolve any target to a DOMRect */
  const resolveTargetRect = useCallback(
    (target: { nodeId?: string; selector?: string }): DOMRect | null => {
      if (target.nodeId) return getNodeRect(target.nodeId);
      if (target.selector) return getSelectorRect(target.selector);
      return null;
    },
    [getNodeRect, getSelectorRect],
  );

  return {
    getNodeScreenCenter,
    getNodeRect,
    getSelectorRect,
    getSelectorCenter,
    resolveTargetCenter,
    resolveTargetRect,
    flowToScreen,
    getViewport,
    fitView,
  };
}
